import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWindowWidth } from '@shared/hooks/index';
import { usePartitionsQuery } from '@src/api/api-hooks/common/usePartitionsQuery.ts';
import { useTagsQuery } from '@src/api/api-hooks/common/useTagsQuery.ts';

export const useCoachesList = () => {
  const isSearchingState = useState(false);

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;

  const [searchParams, setSearchParams] = useSearchParams();

  const isFilterOpenState = useState(searchParams.size > 0);

  const [selectedTags, setSelectedTags] = useState(searchParams.get('tags') || '');
  const [searchCity, setSearchCity] = useState(searchParams.get('search') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('selectedCity') || '');
  const [searchByName, setSearchByName] = useState(searchParams.get('searchByName') || '');
  const [selectedPartitions, setSelectedPartitions] = useState<number[]>(
    searchParams.getAll('partitions').map(Number) ?? []
  );

  const [consultationsType, setConsultationsType] = useState(
    searchParams.get('consultations_type') || ''
  );

  const { data: partitions } = usePartitionsQuery();
  const { data: tags } = useTagsQuery({ partition: selectedPartitions?.at(0) });

  useEffect(() => {
    const params: Record<string, string> = {};

    selectedTags && (params.selectedTags = selectedTags);
    searchCity && (params.searchCity = searchCity);
    selectedCity && (params.selectedCity = selectedCity);
    consultationsType && (params.consultationsType = consultationsType);
    selectedPartitions.length && (params.partitions = selectedPartitions.join(' ,'));

    setSearchParams(params);
  }, [
    setSearchParams,
    selectedCity,
    selectedTags,
    searchCity,
    consultationsType,
    selectedPartitions,
  ]);

  const resetAllParams = useCallback(() => {
    setSelectedCity('');
    setSearchCity('');
    setConsultationsType('');
    setSelectedTags('');
    setSearchParams();
    setSelectedPartitions([]);
  }, [setSearchParams, setSelectedCity, setSearchCity, setConsultationsType, setSelectedTags]);

  useEffect(() => {
    searchCity === '' && setSelectedCity('');
  }, [searchCity, setSelectedCity]);

  const doSelectCity = useCallback(
    (id: number) => {
      setSelectedCity(String(id));
    },
    [setSelectedCity]
  );

  const setStateWithCleanPage = (action: Dispatch<SetStateAction<string>>) => {
    return (val: SetStateAction<string>) => {
      action(val);
    };
  };

  return {
    searchByName,
    setSearchByName: setStateWithCleanPage(setSearchByName),
    isFilterOpenState,
    isSearchingState,
    resetAllParams,
    doSelectCity,
    consultationsType,
    setConsultationsType: setStateWithCleanPage(setConsultationsType),
    searchCity,
    setSearchCity: setStateWithCleanPage(setSearchCity),
    selectedTags,
    setSelectedTags: setStateWithCleanPage(setSelectedTags),
    isMobile,
    selectedCity,
    selectedPartitions,
    setSelectedPartitions,
    partitions,
    tags,
  };
};
